import { ReactNode } from 'react';
import { add, createOutline } from 'ionicons/icons';
import { PlanningGroup, PlanningLineItem } from '@/services/PlanningService';
import { ActionAccordionRowData } from '@/types/model/actionAccordion';
import { createActionAccordionAdapter } from '@/services/util/adapters/ActionAccordionAdapter';

interface BuildPlanningActionAccordionRowsParams {
  groups: PlanningGroup[];
  previousRows?: ActionAccordionRowData[];
  canAddLineItem: boolean;
  onEditGroup: (group: PlanningGroup) => void;
  onAddLineItem: (groupId: number) => void;
  onEditLineItem: (lineItem: PlanningLineItem, groupId: number) => void;
  renderLineItemContent: (lineItem: PlanningLineItem) => ReactNode;
}

const rowStateKey = (type: 'Group' | 'LineItem', id: number): string => `${type}:${id}`;

const isPlanningGroup = (source: PlanningGroup | PlanningLineItem): source is PlanningGroup => {
  return 'groupIdentifier' in source;
};

const collectPreviousState = (
  rows: ActionAccordionRowData[]
): Record<string, { isExpanded: boolean; isSelected: boolean }> => {
  const state: Record<string, { isExpanded: boolean; isSelected: boolean }> = {};

  const visit = (currentRows: ActionAccordionRowData[]) => {
    for (const row of currentRows) {
      state[rowStateKey(row.type, row.id)] = {
        isExpanded: row.isExpanded,
        isSelected: Boolean(row.isSelected),
      };

      if (row.children && row.children.length > 0) {
        visit(row.children);
      }
    }
  };

  visit(rows);
  return state;
};

export const buildPlanningActionAccordionRows = ({
  groups,
  previousRows,
  canAddLineItem,
  onEditGroup,
  onAddLineItem,
  onEditLineItem,
  renderLineItemContent,
}: BuildPlanningActionAccordionRowsParams): ActionAccordionRowData[] => {
  const previousState = previousRows ? collectPreviousState(previousRows) : {};

  const adapter = createActionAccordionAdapter<PlanningGroup | PlanningLineItem>({
    getChildren: (source) => {
      if (isPlanningGroup(source)) {
        return source.lineItems ?? undefined;
      }

      return undefined;
    },
    mapRow: ({ source, parent }) => {
      if (isPlanningGroup(source)) {
        const saved = previousState[rowStateKey('Group', source.id)];

        return {
          id: source.id,
          type: 'Group',
          number: String(source.groupIdentifier),
          title: source.title,
          isExpanded: saved?.isExpanded ?? false,
          isSelected: saved?.isSelected ?? false,
          isSelectable: (source.lineItems?.length ?? 0) > 0,
          additionalColumns: [
            {
              id: 'edit-group',
              kind: 'action' as const,
              icon: createOutline,
              title: 'Edit Group',
              onAction: () => onEditGroup(source),
            },
            ...(canAddLineItem
              ? [{
                  id: 'add-line-item',
                  kind: 'action' as const,
                  icon: add,
                  title: 'New Line Item Under Group',
                  onAction: () => onAddLineItem(source.id),
                }]
              : []),
          ],
        };
      }

      const lineItem = source as PlanningLineItem;
      const parentGroup = parent as PlanningGroup | undefined;
      const parentGroupId = parentGroup?.id ?? 0;
      const saved = previousState[rowStateKey('LineItem', lineItem.id)];

      return {
        id: lineItem.id,
        type: 'LineItem',
        number: lineItem.lineItem,
        title: lineItem.title,
        isActive: lineItem.isActive,
        isExpanded: saved?.isExpanded ?? false,
        isExpandable: true,
        isSelected: saved?.isSelected ?? false,
        isSelectable: true,
        additionalColumns: [
          {
            id: 'edit-line-item',
            kind: 'action' as const,
            icon: createOutline,
            title: 'Edit Line Item',
            onAction: () => onEditLineItem(lineItem, parentGroupId),
          },
        ],
        content: renderLineItemContent(lineItem),
      };
    },
  });

  return adapter.toRows(groups);
};
